import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, FileText, Files, BarChart3, Lock, AlertCircle, MinusCircle } from "lucide-react";
import merchantApi from "../../lib/merchantApi";
import { useMerchantAuth } from "../../context/MerchantAuthContext";
import { useMerchantApplication } from "../../context/MerchantApplicationContext";
import StatusPill from "./components/StatusPill";

const TERMINAL_STATES = new Set(["submitted", "under_review", "approved", "live"]);

export default function Dashboard() {
  const { user } = useMerchantAuth();
  const { dashboard, loading, refreshDashboard } = useMerchantApplication();
  const [launching, setLaunching] = useState(false);

  useEffect(() => { refreshDashboard(); }, [refreshDashboard]);

  const launchGateway = async () => {
    if (launching) return;
    setLaunching(true);
    try {
      const { data } = await merchantApi.post("/merchant/app/gateway/open");
      // Open AFTER the audit write succeeds — this guarantees the audit log
      // never silently misses opens. The latency is one POST (<200ms) so
      // the UX cost is negligible.
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      // Fall back to the URL we already have so a transient audit failure
      // doesn't lock the merchant out of their gateway.
      const fallback = dashboard?.application?.gateway_url || "https://gateway.neptunepay.io";
      window.open(fallback, "_blank", "noopener,noreferrer");
    } finally {
      setLaunching(false);
    }
  };

  if (loading || !dashboard) {
    return (
      <div data-testid="dashboard-loading" className="text-zinc-500 text-[11px] tracking-[0.22em] uppercase">
        Loading dashboard…
      </div>
    );
  }

  const { application, onboarding, documents, latest_report, next_steps } = dashboard;
  const company = application.company_name || "Your account";
  const onboardingPct = Math.round((onboarding.steps_complete / onboarding.total_steps) * 100);
  const submitted = TERMINAL_STATES.has(application.status);
  const isLive = application.status === "live";

  return (
    <div data-testid="dashboard-page" className="max-w-6xl space-y-8">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-3xl border border-white/[0.06] bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-6 sm:p-8"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Welcome back</div>
            <div className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-[1.1] truncate">
              {company}
            </div>
            <div className="mt-2 text-[12.5px] text-zinc-400">{user?.email}</div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <StatusPill status={application.status} />
            <div data-testid="dashboard-ref" className="ref-mono text-[11.5px] text-zinc-500">
              {application.ref}
            </div>
            {isLive && (
              <button
                type="button"
                onClick={launchGateway}
                disabled={launching}
                data-testid="dashboard-launch-gateway-btn"
                className="mt-3 group inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-50 transition-colors px-4 py-2 text-[13px] font-medium"
              >
                {launching ? "Opening…" : "Launch gateway"}
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
            )}
          </div>
        </div>
      </motion.section>

      {application.status === "info_requested" && application.info_request && (
        <MerchantInfoRequestBanner payload={application.info_request} />
      )}
      {application.status === "declined" && application.decline_reason && (
        <MerchantDeclineBanner payload={application.decline_reason} />
      )}

      {/* Three primary cards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          testId="dashboard-card-onboarding"
          eyebrow="Onboarding"
          icon={FileText}
          title={`${onboarding.steps_complete} of ${onboarding.total_steps} steps`}
        >
          <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              data-testid="dashboard-onboarding-progress"
              style={{ width: `${onboardingPct}%` }}
              className="h-full bg-gradient-to-r from-[#00E5FF] to-[#0055FF] transition-[width] duration-700 ease-out"
            />
          </div>
          <Link
            to="/app/onboarding"
            data-testid="dashboard-onboarding-cta"
            className="group mt-5 inline-flex items-center gap-1.5 text-[13px] text-white hover:text-[#00E5FF] transition-colors"
          >
            {submitted ? "View application" : "Continue onboarding"}
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Card>

        <Card
          testId="dashboard-card-documents"
          eyebrow="Documents"
          icon={Files}
          title={`${documents.uploaded} of ${documents.required_total} required`}
        >
          <div className="mt-3 text-[12px] text-zinc-500 leading-relaxed">
            {documents.marked_na > 0 ? `${documents.marked_na} marked N/A · ` : ""}
            Upload any outstanding documents to keep things moving.
          </div>
          <Link
            to="/app/onboarding"
            data-testid="dashboard-documents-cta"
            className="group mt-5 inline-flex items-center gap-1.5 text-[13px] text-white hover:text-[#00E5FF] transition-colors"
          >
            Upload documents
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </Card>

        <Card
          testId="dashboard-card-report"
          eyebrow="Latest report"
          icon={BarChart3}
          title={latest_report ? latest_report.title : "No reports yet"}
        >
          {latest_report ? (
            <>
              <div className="mt-3 flex items-center gap-2 text-[12px] text-zinc-500">
                <span className="inline-flex items-center rounded-md bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 text-[10.5px] uppercase tracking-[0.1em] text-zinc-300">
                  {latest_report.tab.replace(/_/g, " ")}
                </span>
                <span>· {latest_report.period}</span>
              </div>
              <div className="mt-1 text-[11.5px] text-zinc-600">
                Uploaded {new Date(latest_report.uploaded_at).toLocaleDateString()}
              </div>
              <Link
                to="/app/financials"
                data-testid="dashboard-report-cta"
                className="group mt-5 inline-flex items-center gap-1.5 text-[13px] text-white hover:text-[#00E5FF] transition-colors"
              >
                Open Financials
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </>
          ) : (
            <div className="mt-3 text-[12px] text-zinc-500 leading-relaxed">
              Your team will share these once you're live.
            </div>
          )}
        </Card>
      </section>

      {/* Next steps */}
      <section
        data-testid="dashboard-next-steps"
        className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-6 sm:p-7"
      >
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Next steps</div>
        <ul className="mt-4 space-y-3">
          {next_steps.map((step, i) => (
            <li
              key={i}
              data-testid={`dashboard-next-step-${i}`}
              className="flex items-start gap-3 text-[14px] text-zinc-200 leading-relaxed"
            >
              <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#0055FF] shrink-0" />
              {step}
            </li>
          ))}
        </ul>
        {application.status === "draft" && (
          <div className="mt-6 flex items-center gap-3 text-[12px] text-zinc-500">
            <Lock className="h-3.5 w-3.5" />
            Gateway access unlocks once your application is approved and live.
          </div>
        )}
      </section>
    </div>
  );
}

function Card({ testId, eyebrow, icon: Icon, title, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testId}
      className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.03] hover:border-white/[0.12] transition-colors p-5"
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— {eyebrow}</div>
        <Icon className="h-3.5 w-3.5 text-[#00E5FF]" strokeWidth={1.6} />
      </div>
      <div className="mt-3 font-display text-[20px] font-semibold tracking-tight text-white leading-snug">
        {title}
      </div>
      {children}
    </motion.div>
  );
}


function MerchantInfoRequestBanner({ payload }) {
  const cats = payload?.categories || [];
  const items = payload?.items || [];
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="merchant-info-request-banner"
      className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-5"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300">— More info needed</div>
          <h2 className="mt-1.5 font-display text-[20px] sm:text-[22px] tracking-tight text-amber-50">
            We need a little more from you
          </h2>
          {cats.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {cats.map((c) => (
                <span key={c} className="rounded-full border border-amber-400/40 bg-amber-400/[0.10] px-2.5 py-0.5 text-[11.5px] text-amber-100">{c.replace("_", " ")}</span>
              ))}
            </div>
          )}
          {items.length > 0 && (
            <ul className="mt-3 list-disc list-inside text-[13.5px] text-amber-50 space-y-1">
              {items.map((it, i) => <li key={i} className="break-words" data-testid={`merchant-info-request-item-${i}`}>{it}</li>)}
            </ul>
          )}
          {payload?.free_text && (
            <div className="mt-3 text-[13.5px] text-amber-50/90 whitespace-pre-wrap break-words" data-testid="merchant-info-request-free-text">
              {payload.free_text}
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}


function MerchantDeclineBanner({ payload }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="merchant-decline-banner"
      className="rounded-2xl border border-rose-400/30 bg-rose-400/[0.05] p-5"
    >
      <div className="flex items-start gap-3">
        <MinusCircle className="h-5 w-5 text-rose-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.22em] text-rose-300">— Application not approved</div>
          <h2 className="mt-1.5 font-display text-[20px] sm:text-[22px] tracking-tight text-rose-50">
            Your application was not approved at this time
          </h2>
          <div className="mt-3 text-[13.5px] text-rose-50/90 whitespace-pre-wrap break-words" data-testid="merchant-decline-reason">
            {payload.reason}
          </div>
          <div className="mt-3 text-[12px] text-rose-200/70">
            Please contact your account exec at Neptune if you'd like to discuss next steps.
          </div>
        </div>
      </div>
    </motion.section>
  );
}
